// ONU card — the React port of search.js's `renderOnuCard`. Reads the
// poller's per-ONU snapshot (the pass-through `onu` blob from
// /v1/api/search → internal/olt/types.go ONURecord) and lays it out like
// the PHP card: a status pill (Online / Fiber Down / Offline), the OLT
// location line, then optical + hardware + LAN rows. Unknown extra keys
// are dumped at the bottom so nothing the poller adds is silently lost.

import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Badge, CopyButton, DataCard, DataGrid, DataItem, cn, type BadgeProps } from "@noc/ui";
import { OnuBindActions } from "./OnuBindActions";
import { fmtAgo, fmtUptimeSeconds, rxPowerToneClass } from "./format";

// Keys we render explicitly (or intentionally hide) — everything else in
// the blob falls through to the generic "Other" rows.
const HANDLED = new Set([
  "olt",
  "olt_ip",
  "pon_port",
  "onu_index",
  "username",
  "is_online",
  "updated_at",
  "dereg_reason",
  "serial",
  "model",
  "firmware",
  "rx_power_dbm",
  "tx_power_dbm",
  "temp_c",
  "voltage_v",
  "bias_ma",
  "distance_m",
  "uptime_seconds",
  "lan_status",
  "lan_speed",
  "cpe_mac",
  "last_online",
  "last_offline",
]);

function s(v: unknown): string {
  return v === null || v === undefined ? "" : String(v);
}
function n(v: unknown): number {
  return typeof v === "number" ? v : Number.parseFloat(String(v ?? "")) || 0;
}

export function OnuCard({
  onu,
  subscriber,
  busy,
  title = "ONU — live SSH",
  compact = false,
  collapsible = false,
}: {
  onu: Record<string, unknown>;
  /** The subscriber this ONU is expected to belong to — enables the
   *  "Bind to <user>" action when the ONU's desc doesn't match. Omit
   *  (e.g. for a switch box's own uplink ONU) to render read-only. */
  subscriber?: string;
  /** True while the live over-SSH detail fetch is in flight (shows a header spinner). */
  busy?: boolean;
  /** Card heading; defaults to "ONU — live SSH". */
  title?: string;
  /** Lay the readings out in a responsive multi-column grid instead of a
   *  vertical stack — for full-width contexts (the switch-users modal). */
  compact?: boolean;
  /** Render the readings collapsed behind a toggle (the header — status +
   *  OLT/PON/ONU line — always shows). For supplementary placements like
   *  the switch-users modal where the card shouldn't dominate the height. */
  collapsible?: boolean;
}) {
  const [open, setOpen] = useState(!collapsible);
  const olt = s(onu.olt);
  const ponPort = s(onu.pon_port);
  const onuIndex = s(onu.onu_index);
  const isOnline = onu.is_online === true;
  const serial = s(onu.serial);
  const model = s(onu.model);
  const firmware = s(onu.firmware);
  const rxDbm = s(onu.rx_power_dbm);
  const txDbm = s(onu.tx_power_dbm);
  const tempC = s(onu.temp_c);
  const voltageV = s(onu.voltage_v);
  const biasMA = s(onu.bias_ma);
  const distanceM = n(onu.distance_m);
  const uptimeSeconds = n(onu.uptime_seconds);
  const lanStatus = s(onu.lan_status);
  const lanSpeed = s(onu.lan_speed);
  const cpeMac = s(onu.cpe_mac);
  const lastOnline = s(onu.last_online);
  const lastOffline = s(onu.last_offline);
  const deregReason = onu.dereg_reason && onu.dereg_reason !== "N/A" ? s(onu.dereg_reason) : "";

  const isLOS = !isOnline && /los/i.test(deregReason);
  const statusLabel = isOnline ? "Online" : isLOS ? "Fiber Down" : deregReason || "Offline";
  const statusVariant: NonNullable<BadgeProps["variant"]> = isOnline
    ? "success"
    : isLOS
      ? "warning"
      : "destructive";

  const serialModel = serial && model ? `${serial} / ${model}` : serial || model;

  const extra = Object.keys(onu).filter((k) => !HANDLED.has(k) && s(onu[k]) !== "");

  const rows = (
    <>
      {rxDbm && (
        <DataItem label="RX power" mono className={rxPowerToneClass(rxDbm)}>
          {rxDbm} dBm
        </DataItem>
      )}
      {txDbm && (
        <DataItem label="TX power" mono>
          {txDbm} dBm
        </DataItem>
      )}
      {tempC && (
        <DataItem label="Temperature" mono>
          {tempC} °C
        </DataItem>
      )}
      {voltageV && (
        <DataItem label="Voltage" mono>
          {voltageV} V
        </DataItem>
      )}
      {biasMA && (
        <DataItem label="Bias" mono>
          {biasMA} mA
        </DataItem>
      )}
      {serialModel && (
        <DataItem label="Serial / Model" mono>
          {serialModel}
        </DataItem>
      )}
      {firmware && (
        <DataItem label="Firmware" mono>
          {firmware}
        </DataItem>
      )}
      {distanceM > 0 && (
        <DataItem label="Distance" mono>
          {distanceM} m <span className="text-muted-foreground">({(distanceM / 1000).toFixed(2)} km)</span>
        </DataItem>
      )}
      {uptimeSeconds > 0 && (
        <DataItem label="ONU uptime" mono>
          {fmtUptimeSeconds(uptimeSeconds)}
        </DataItem>
      )}
      {lanStatus && (
        <DataItem label="LAN port">
          <Badge variant={lanStatus.toLowerCase() === "up" ? "success" : "destructive"}>{lanStatus}</Badge>
          {lanSpeed && <span className="text-muted-foreground">{lanSpeed}</span>}
        </DataItem>
      )}
      {cpeMac && (
        <DataItem label="CPE MAC" mono>
          {cpeMac}
          <CopyButton value={cpeMac} label="CPE MAC" />
        </DataItem>
      )}
      {lastOnline && <DataItem label="Last online">{fmtAgo(lastOnline)}</DataItem>}
      {!isOnline && lastOffline && <DataItem label="Last offline">{fmtAgo(lastOffline)}</DataItem>}
      {extra.map((k) => (
        <DataItem key={k} label={prettyKey(k)} mono className={cn(typeof onu[k] === "boolean" && "font-sans")}>
          {fmtVal(onu[k])}
        </DataItem>
      ))}
    </>
  );

  return (
    <DataCard
      title={title}
      subtitle={olt || ponPort ? `${olt}${olt && (ponPort || onuIndex) ? " · " : ""}PON ${ponPort || "—"}/ONU ${onuIndex || "—"}` : undefined}
      badge={
        <Badge variant={statusVariant} className="uppercase">
          {statusLabel}
        </Badge>
      }
      action={
        collapsible ? (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
            aria-expanded={open}
          >
            {open ? "Hide detail" : "Optical detail"}
            <ChevronDown className={cn("size-3.5 transition-transform", open && "rotate-180")} />
          </button>
        ) : subscriber ? (
          <OnuBindActions subscriber={subscriber} onu={onu} />
        ) : undefined
      }
      busy={busy}
    >
      {open &&
        (compact ? (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5 sm:grid-cols-[repeat(auto-fill,minmax(9rem,1fr))]">{rows}</dl>
        ) : (
          <DataGrid>{rows}</DataGrid>
        ))}
    </DataCard>
  );
}

function prettyKey(k: string): string {
  return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
function fmtVal(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "yes" : "no";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}
